import { id as orderDateId } from '../../../filters/options/order-date';
import { id as orderNumberId } from '../../../filters/options/order-number';
import { id as recipientNameId } from '../../../filters/options/recipient-name';
import { id as emailId } from '../../../filters/options/email';
import { id as itemNameId } from '../../../filters/options/item-name';
import { id as itemSkuId } from '../../../filters/options/item-sku';
import { id as itemOptionId } from '../../../filters/options/item-option';
import { id as orderStatusId } from '../../../filters/options/order-status';
import { id as storeId } from '../../../filters/options/store';
import { contains } from '../../../filters/operators/string';
import { isOperator } from '../../../filters/operators/collection';
import {
  AWAITING_PAYMENT,
  AWAITING_SHIPMENT,
  CANCELLED,
  ON_HOLD,
  PENDING_FULFILLMENT,
  REJECTED_FULFILLMENT,
  SHIPPED,
} from '../../../services/api/order-status-from-view-service';
/**
 * Route state key for Shippy search filters
 */
export const SHIPPY_FILTERS_STATE_KEY = 'shippyFilters';

/**
 * Maps AI capability status values to Advanced Search API status values
 * These must match the apiKey values used in filters/options/order-status/options.js
 */
export const STATUS_VALUE_MAP = {
  awaiting_shipment: AWAITING_SHIPMENT,
  shipped: SHIPPED,
  on_hold: ON_HOLD,
  awaiting_payment: AWAITING_PAYMENT,
  cancelled: CANCELLED,
  pending_fulfillment: PENDING_FULFILLMENT,
  rejected_fulfillment: REJECTED_FULFILLMENT,
};

type StringFilterMapping = {
  param: string;
  filterId: string;
};

export const STRING_FILTER_MAPPINGS: StringFilterMapping[] = [
  { param: 'orderNumber', filterId: orderNumberId },
  { param: 'recipientName', filterId: recipientNameId },
  { param: 'email', filterId: emailId },
  { param: 'itemName', filterId: itemNameId },
  { param: 'itemSku', filterId: itemSkuId },
  { param: 'itemOption', filterId: itemOptionId },
];

/**
 * Filter IDs for reference
 */
export const FILTER_IDS = {
  orderDate: orderDateId,
  orderNumber: orderNumberId,
  recipientName: recipientNameId,
  email: emailId,
  itemName: itemNameId,
  itemSku: itemSkuId,
  itemOption: itemOptionId,
  orderStatus: orderStatusId,
  storeGuid: storeId,
};

/**
 * Filter operators
 */
export const FILTER_OPERATORS = {
  contains,
  is: isOperator,
};

/**
 * SessionStorage key for syncing active search filters to Shippy context
 */
export const CURRENT_SEARCH_FILTERS_STORAGE_KEY = 'shippy-current-search-filters';

type FilterObject = {
  id: string;
  value: unknown;
  operator?: unknown;
  source?: string;
};

type DateValue = {
  from?: string;
  to?: string;
};

/**
 * Reverse STATUS_VALUE_MAP: internal API status values → AI param values
 */
const REVERSE_STATUS_VALUE_MAP = Object.fromEntries(
  Object.entries(STATUS_VALUE_MAP).map(([aiKey, internalValue]) => [internalValue, aiKey]),
);

/**
 * Reverse STRING_FILTER_MAPPINGS: filterId → AI param name
 */
const REVERSE_STRING_FILTER_MAP = Object.fromEntries(STRING_FILTER_MAPPINGS.map(({ param, filterId }) => [filterId, param]));

/**
 * Convert internal filter objects back to the AI parameter format.
 * This allows Shippy to see what filters are currently active for follow-up conversations.
 */
export const reverseMapFiltersToParams = (filters: FilterObject[]): Record<string, unknown> => {
  if (!Array.isArray(filters) || filters.length === 0) {
    return {};
  }

  const params: Record<string, unknown> = {};

  for (const filter of filters) {
    const { id, value } = filter;

    // String filters (orderNumber, recipientName, email, itemName, itemSku, itemOption)
    const stringParam = REVERSE_STRING_FILTER_MAP[id];
    if (stringParam) {
      params[stringParam] = value;
      continue;
    }

    // Order status
    if (id === orderStatusId) {
      const statusValues = Array.isArray(value) ? value : [value];
      const aiStatuses = statusValues.map((s: string) => REVERSE_STATUS_VALUE_MAP[s]).filter(Boolean);
      if (aiStatuses.length > 0) {
        params.orderStatus = aiStatuses;
      }
      continue;
    }

    // Store
    if (id === storeId) {
      const storeValues = (Array.isArray(value) ? value : [value]).filter(Boolean);
      if (storeValues.length > 0) {
        params.storeGuids = storeValues;
      }
      continue;
    }

    // Date range
    if (id === orderDateId) {
      const dateValue = value as DateValue;
      if (dateValue && typeof dateValue === 'object') {
        if (dateValue.from) {
          params.orderDateFrom = dateValue.from;
        }
        if (dateValue.to) {
          params.orderDateTo = dateValue.to;
        }
      }
      continue;
    }
  }

  return params;
};
